import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export type FohStockWarningBehavior = 'warn_allow' | 'block_hard' | 'require_pin';

export interface LocationSettings {
  foh_stock_warning_behavior?: FohStockWarningBehavior;
  appearance?: Record<string, unknown>;
  icon?: string;
  icon_url?: string;
  hide_default_payment_types?: boolean;
}

interface UpdateLocationSettingInput {
  key: keyof LocationSettings;
  value: LocationSettings[keyof LocationSettings];
  locationId?: string;
}

const DEFAULT_FOH_STOCK_WARNING_BEHAVIOR: FohStockWarningBehavior = 'require_pin';

export function useLocationSettings() {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();

  // Fetch current location settings
  const { data: locationSettings, isLoading } = useQuery({
    queryKey: ['location-settings', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return null;

      const { data, error } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', currentLocation.id)
        .single();

      if (error) throw error;
      return (data?.settings as LocationSettings) || {};
    },
    enabled: !!currentLocation?.id,
  });

  // Get the effective FOH stock warning behavior
  const fohStockWarningBehavior: FohStockWarningBehavior =
    locationSettings?.foh_stock_warning_behavior || DEFAULT_FOH_STOCK_WARNING_BEHAVIOR;

  // Update a specific location setting
  const updateLocationSetting = useMutation({
    mutationFn: async ({ key, value, locationId }: UpdateLocationSettingInput) => {
      const targetLocationId = locationId || currentLocation?.id;
      if (!targetLocationId) throw new Error('No location selected');

      // Fetch current settings
      const { data: currentData, error: fetchError } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', targetLocationId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = (currentData?.settings as LocationSettings) || {};
      const newSettings: LocationSettings = {
        ...currentSettings,
        [key]: value,
      };

      const { error } = await supabase
        .from('locations')
        .update({ settings: newSettings })
        .eq('id', targetLocationId);

      if (error) throw error;
      return { key, value };
    },
    onSuccess: (_, variables) => {
      const targetLocationId = variables.locationId || currentLocation?.id;
      queryClient.invalidateQueries({ queryKey: ['location-settings', targetLocationId] });
    },
  });

  // Convenience method for updating FOH stock warning behavior
  const updateFohStockWarningBehavior = async (
    behavior: FohStockWarningBehavior,
    locationId?: string
  ) => {
    return updateLocationSetting.mutateAsync({
      key: 'foh_stock_warning_behavior',
      value: behavior,
      locationId,
    });
  };

  return {
    // Settings
    locationSettings,
    fohStockWarningBehavior,
    isLoading,

    // Mutations
    updateLocationSetting,
    updateFohStockWarningBehavior,
    isUpdating: updateLocationSetting.isPending,
  };
}
